import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import styles from './EmailVerification.module.scss';

interface EmailVerificationProps {
  email: string;
  onSuccess: () => void;
  selectedPlan?: string;
  planPrice?: number;
  billingPeriod?: string;
}

export const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  onSuccess,
  selectedPlan,
  planPrice,
  billingPeriod,
}) => {
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();

  const verifyCode = async () => {
    if (!code.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          code: code.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Verification failed');
      }

      toast.success('Email verified successfully!');
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const resendCode = async () => {
    setIsResending(true);
    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to resend code');
      }

      toast.success('New verification code sent!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      verifyCode();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Verify your email</h1>
        <p className={styles.subtitle}>
          We've sent a 6-digit code to <strong>{email}</strong>
        </p>
      </div>

      <div className={styles.form}>
        <div className={styles.inputGroup}>
          <input
            type="text"
            placeholder="Enter 6-digit code"
            className={styles.codeInput}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyPress={handleKeyPress}
            maxLength={6}
            autoFocus
          />
        </div>

        <button
          onClick={verifyCode}
          disabled={isVerifying || code.length !== 6}
          className={styles.verifyButton}
        >
          {isVerifying ? 'Verifying...' : 'Verify Email'}
        </button>

        <div className={styles.resendSection}>
          <p className={styles.resendText}>
            Didn't receive the code?{' '}
            <button
              onClick={resendCode}
              disabled={isResending}
              className={styles.resendButton}
            >
              {isResending ? 'Sending...' : 'Resend'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};